import { BookingCardTheme } from './themesCrafted';
import { formatDateLong, formatTime } from '@/utils/dateFormatting';
import { Calendar, Clock, MapPin, Hash } from 'lucide-react';

export interface BookingCardGradientBackProps {
  serviceName: string | null;
  appointmentDate: string;
  appointmentTime: string;
  locationLabel: string | null;
  publicConfirmationCode: string;
  theme: BookingCardTheme;
}

export function BookingCardGradientBack({
  serviceName,
  appointmentDate,
  appointmentTime,
  locationLabel,
  publicConfirmationCode,
  theme,
}: BookingCardGradientBackProps) {
  const formattedDate = formatDateLong(appointmentDate);
  const formattedTime = formatTime(appointmentTime);

  return (
    <div
      className="relative w-full h-full rounded-3xl overflow-hidden p-6 flex flex-col"
      style={{
        background: theme.gradientFrom,
        border: `1px solid ${theme.borderColor}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16), 0 12px 40px rgba(0, 0, 0, 0.24), 0 0 1px rgba(0, 0, 0, 0.2)',
      }}
    >
      {/* Title */}
      <div
        className="font-bold mb-6"
        style={{
          fontSize: 'clamp(1.125rem, 2.5vw + 0.5rem, 1.25rem)',
          color: theme.textPrimary,
        }}
      >
        Booking Details
      </div>

      {/* Details */}
      <div className="flex-1 flex flex-col gap-4">
        {/* Service Row */}
        {serviceName && (
          <div className="flex items-start gap-3">
            <div
              className="rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: theme.borderColor,
                width: 'clamp(1.75rem, 3vw, 2rem)',
                height: 'clamp(1.75rem, 3vw, 2rem)',
              }}
            >
              <Hash
                style={{
                  color: theme.textPrimary,
                  width: 'clamp(0.875rem, 1.5vw, 1rem)',
                  height: 'clamp(0.875rem, 1.5vw, 1rem)',
                }}
              />
            </div>
            <div className="flex flex-col">
              <div
                className="uppercase tracking-wider mb-1"
                style={{
                  fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
                  color: theme.textSecondary,
                }}
              >
                SERVICE
              </div>
              <div
                className="font-semibold"
                style={{
                  fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                  color: theme.textPrimary,
                }}
              >
                {serviceName}
              </div>
            </div>
          </div>
        )}

        {/* Date Row */}
        <div className="flex items-start gap-3">
          <div
            className="rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: theme.borderColor,
              width: 'clamp(1.75rem, 3vw, 2rem)',
              height: 'clamp(1.75rem, 3vw, 2rem)',
            }}
          >
            <Calendar
              style={{
                color: theme.textPrimary,
                width: 'clamp(0.875rem, 1.5vw, 1rem)',
                height: 'clamp(0.875rem, 1.5vw, 1rem)',
              }}
            />
          </div>
          <div className="flex flex-col">
            <div
              className="uppercase tracking-wider mb-1"
              style={{
                fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
                color: theme.textSecondary,
              }}
            >
              DATE
            </div>
            <div
              className="font-semibold"
              style={{
                fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                color: theme.textPrimary,
              }}
            >
              {formattedDate}
            </div>
          </div>
        </div>

        {/* Time Row */}
        <div className="flex items-start gap-3">
          <div
            className="rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: theme.borderColor,
              width: 'clamp(1.75rem, 3vw, 2rem)',
              height: 'clamp(1.75rem, 3vw, 2rem)',
            }}
          >
            <Clock
              style={{
                color: theme.textPrimary,
                width: 'clamp(0.875rem, 1.5vw, 1rem)',
                height: 'clamp(0.875rem, 1.5vw, 1rem)',
              }}
            />
          </div>
          <div className="flex flex-col">
            <div
              className="uppercase tracking-wider mb-1"
              style={{
                fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
                color: theme.textSecondary,
              }}
            >
              TIME
            </div>
            <div
              className="font-semibold"
              style={{
                fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                color: theme.textPrimary,
              }}
            >
              {formattedTime}
            </div>
          </div>
        </div>

        {/* Location Row */}
        {locationLabel && (
          <div className="flex items-start gap-3">
            <div
              className="rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: theme.borderColor,
                width: 'clamp(1.75rem, 3vw, 2rem)',
                height: 'clamp(1.75rem, 3vw, 2rem)',
              }}
            >
              <MapPin
                style={{
                  color: theme.textPrimary,
                  width: 'clamp(0.875rem, 1.5vw, 1rem)',
                  height: 'clamp(0.875rem, 1.5vw, 1rem)',
                }}
              />
            </div>
            <div className="flex flex-col">
              <div
                className="uppercase tracking-wider mb-1"
                style={{
                  fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
                  color: theme.textSecondary,
                }}
              >
                LOCATION
              </div>
              <div
                className="font-semibold"
                style={{
                  fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                  color: theme.textPrimary,
                }}
              >
                {locationLabel}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Code */}
      <div className="mt-auto flex justify-between items-end gap-4">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <Hash
            className="flex-shrink-0"
            style={{
              color: theme.textSecondary,
              width: 'clamp(0.75rem, 1.5vw, 0.875rem)',
              height: 'clamp(0.75rem, 1.5vw, 0.875rem)',
            }}
          />
          <div
            className="font-medium tracking-wide truncate"
            style={{
              fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
              color: theme.textSecondary,
            }}
          >
            {publicConfirmationCode}
          </div>
        </div>
        <div
          className="font-medium tracking-wide text-right flex-shrink-0"
          style={{
            fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
            color: theme.textSecondary,
          }}
        >
          MADE WITH FIGGY
        </div>
      </div>
    </div>
  );
}
